import { Namespace } from '@repo/sdk/models/components';

import type { CompressorAlarmTable } from './compressor-alarm-code.types';

/** The device points the Alarm Code control reads: namespace and key of each. */
export const COMPRESSOR_ALARM_POINTS = {
  alarmCode: { key: 'AlarmCode', namespace: Namespace.ControlPoint },
  compressorType: { key: 'CompressorType', namespace: Namespace.Metric },
  decodeAlarmCode: { key: 'DecodeAlarmCode', namespace: Namespace.Condition },
} as const;

/** `CompressorType` metric values that have an alarm code table, lowercased. */
export const COMPRESSOR_TYPES = {
  FRICK: 'frick',
} as const;

/**
 * Frick micro-panel warning and shutdown messages by `AlarmCode` (register 3070),
 * shortened from the Frick manual. Kept in the manual's order, which is not numeric.
 *
 * Severity: a Warning is a `warning`; so are 52 (the panel unloads and keeps running),
 * 196/197 (an I/O board restarted) and 240 (the harmonic filter keeps running). The
 * rest stop the compressor and are `error`: shutdowns, sensor faults, starter (DBS),
 * VSD and harmonic filter faults, and start or running failures.
 */
export const FRICK_ALARMS: CompressorAlarmTable = {
  3: { message: 'Balance Piston 1 Shutdown', severity: 'error' },
  4: { message: 'Balance Piston 2 Shutdown', severity: 'error' },
  5: { message: 'Balance Piston 3 Shutdown', severity: 'error' },
  6: { message: 'Liquid Slugging Warning', severity: 'warning' },
  7: { message: 'Liquid Slugging Shutdown', severity: 'error' },
  8: { message: 'High Oil Filter Press Warning', severity: 'warning' },
  9: { message: 'High Oil Filter Press Shutdown', severity: 'error' },
  10: { message: 'Missing Comp. Oil Press Warning', severity: 'warning' },
  11: { message: 'Missing Comp. Oil Press Shutdown A', severity: 'error' },
  12: { message: 'Missing Comp. Oil Press Shutdown B', severity: 'error' },
  13: { message: 'Insufficient Main Oil Press Shutdown', severity: 'error' },
  14: { message: 'High Motor Current Shutdown', severity: 'error' },
  15: { message: 'High Motor Current Warning', severity: 'warning' },
  16: { message: 'Low Motor Current Shutdown', severity: 'error' },
  17: { message: 'High Disch Temp Sensor Fault', severity: 'error' },
  18: { message: 'High Disch Temp Shutdown', severity: 'error' },
  19: { message: 'High Disch Temp Warning', severity: 'warning' },
  20: { message: 'High Suction Press Shutdown', severity: 'error' },
  21: { message: 'High Suction Press Warning', severity: 'warning' },
  22: { message: 'Low Separator Temp Shutdown', severity: 'error' },
  23: { message: 'Low Separator Temp Warning', severity: 'warning' },
  24: { message: 'High Comp. Oil Temp Shutdown', severity: 'error' },
  25: { message: 'High Comp. Oil Temp Warning', severity: 'warning' },
  26: { message: 'Low Comp. Oil Temp Shutdown', severity: 'error' },
  27: { message: 'Low Comp. Oil Temp Warning', severity: 'warning' },
  28: { message: 'Low Suction Shutdown - Reg. Mode 1', severity: 'error' },
  29: { message: 'Low Suction Warning - Reg. Mode 1', severity: 'warning' },
  30: { message: 'Low Suction Shutdown - Reg. Mode 2', severity: 'error' },
  31: { message: 'Low Suction Warning - Reg. Mode 2', severity: 'warning' },
  32: { message: 'Low Suction Shutdown - Reg. Mode 3', severity: 'error' },
  33: { message: 'Low Suction Warning - Reg. Mode 3', severity: 'warning' },
  34: { message: 'Low Suction Shutdown - Reg. Mode 4', severity: 'error' },
  35: { message: 'Low Suction Warning - Reg. Mode 4', severity: 'warning' },
  36: { message: 'Reg. Mode 1 Shutdown', severity: 'error' },
  37: { message: 'Reg. Mode 1 Warning', severity: 'warning' },
  38: { message: 'Reg. Mode 2 Shutdown', severity: 'error' },
  39: { message: 'Reg. Mode 2 Warning', severity: 'warning' },
  40: { message: 'Reg. Mode 3 Shutdown', severity: 'error' },
  41: { message: 'Reg. Mode 3 Warning', severity: 'warning' },
  42: { message: 'Reg. Mode 4 Shutdown', severity: 'error' },
  43: { message: 'Reg. Mode 4 Warning', severity: 'warning' },
  44: { message: 'Low RPM Shutdown', severity: 'error' },
  45: { message: 'Low RPM Warning', severity: 'warning' },
  46: { message: 'High RPM Shutdown', severity: 'error' },
  47: { message: 'High RPM Warning', severity: 'warning' },
  48: { message: 'High Manifold Press Shutdown', severity: 'error' },
  49: { message: 'High Manifold Press Warning', severity: 'warning' },
  50: { message: 'Low Main Oil Injection Press Shutdown', severity: 'error' },
  51: { message: 'Oil Level Shutdown', severity: 'error' },
  52: { message: 'Comp. Capacity Unload Alarm', severity: 'warning' },
  53: { message: 'False Running Fail - Motor Amps', severity: 'error' },
  54: { message: 'False Running Fail - Confirmed Running Inp', severity: 'error' },
  55: { message: 'High Limit Disch Press Shutdown', severity: 'error' },
  56: { message: 'High Limit Disch Temp Shutdown', severity: 'error' },
  57: { message: 'High Disch Press Shutdown - Mode 1', severity: 'error' },
  58: { message: 'High Disch Press Warning - Mode 1', severity: 'warning' },
  59: { message: 'High Disch Press Shutdown - Mode 2', severity: 'error' },
  60: { message: 'High Disch Press Warning - Mode 2', severity: 'warning' },
  61: { message: 'Start Failure Shutdown For Eng And Turb', severity: 'error' },
  62: { message: 'High Liquid Level Shutdown', severity: 'error' },
  63: { message: 'Aux Input 1 Shutdown', severity: 'error' },
  64: { message: 'Aux Input 1 Warning', severity: 'warning' },
  65: { message: 'Aux Input 2 Shutdown', severity: 'error' },
  66: { message: 'Aux Input 2 Warning', severity: 'warning' },
  67: { message: 'Aux Input 3 Shutdown', severity: 'error' },
  68: { message: 'Aux Input 3 Warning', severity: 'warning' },
  69: { message: 'Aux Input 4 Shutdown', severity: 'error' },
  70: { message: 'Aux Input 4 Warning', severity: 'warning' },
  71: { message: 'Aux Input 5 Shutdown', severity: 'error' },
  72: { message: 'Aux Input 5 Warning', severity: 'warning' },
  73: { message: 'Aux Input 6 Shutdown', severity: 'error' },
  74: { message: 'Aux Input 6 Warning', severity: 'warning' },
  75: { message: 'Aux Input 7 Shutdown', severity: 'error' },
  76: { message: 'Aux Input 7 Warning', severity: 'warning' },
  77: { message: 'Aux Input 8 Shutdown', severity: 'error' },
  78: { message: 'Aux Input 8 Warning', severity: 'warning' },
  79: { message: 'Aux Input 9 Shutdown', severity: 'error' },
  80: { message: 'Aux Input 9 Warning', severity: 'warning' },
  81: { message: 'Aux Input 10 Shutdown', severity: 'error' },
  82: { message: 'Aux Input 10 Warning', severity: 'warning' },
  83: { message: 'High Aux Analog 1 Shutdown', severity: 'error' },
  84: { message: 'High Aux Analog 1 Warning', severity: 'warning' },
  85: { message: 'Low Aux Analog 1 Shutdown', severity: 'error' },
  86: { message: 'Low Aux Analog 1 Warning', severity: 'warning' },
  87: { message: 'High Aux Analog 2 Shutdown', severity: 'error' },
  88: { message: 'High Aux Analog 2 Warning', severity: 'warning' },
  89: { message: 'Low Aux Analog 2 Shutdown', severity: 'error' },
  90: { message: 'Low Aux Analog 2 Warning', severity: 'warning' },
  91: { message: 'High Aux Analog 3 Shutdown', severity: 'error' },
  92: { message: 'High Aux Analog 3 Warning', severity: 'warning' },
  93: { message: 'Low Aux Analog 3 Shutdown', severity: 'error' },
  94: { message: 'Low Aux Analog 3 Warning', severity: 'warning' },
  95: { message: 'High Aux Analog 4 Shutdown', severity: 'error' },
  96: { message: 'High Aux Analog 4 Warning', severity: 'warning' },
  97: { message: 'Low Aux Analog 4 Shutdown', severity: 'error' },
  98: { message: 'Low Aux Analog 4 Warning', severity: 'warning' },
  99: { message: 'High Aux Analog 5 Shutdown', severity: 'error' },
  100: { message: 'High Aux Analog 5 Warning', severity: 'warning' },
  101: { message: 'Low Aux Analog 5 Shutdown', severity: 'error' },
  102: { message: 'Low Aux Analog 5 Warning', severity: 'warning' },
  103: { message: 'High Aux Analog 6 Shutdown', severity: 'error' },
  104: { message: 'High Aux Analog 6 Warning', severity: 'warning' },
  105: { message: 'Low Aux Analog 6 Shutdown', severity: 'error' },
  106: { message: 'Low Aux Analog 6 Warning', severity: 'warning' },
  107: { message: 'High Aux Analog 7 Shutdown', severity: 'error' },
  108: { message: 'High Aux Analog 7 Warning', severity: 'warning' },
  109: { message: 'Low Aux Analog 7 Shutdown', severity: 'error' },
  110: { message: 'Low Aux Analog 7 Warning', severity: 'warning' },
  111: { message: 'High Aux Analog 8 Shutdown', severity: 'error' },
  112: { message: 'High Aux Analog 8 Warning', severity: 'warning' },
  113: { message: 'Low Aux Analog 8 Shutdown', severity: 'error' },
  114: { message: 'Low Aux Analog 8 Warning', severity: 'warning' },
  115: { message: 'High Aux Analog 9 Shutdown', severity: 'error' },
  116: { message: 'High Aux Analog 9 Warning', severity: 'warning' },
  117: { message: 'Low Aux Analog 9 Shutdown', severity: 'error' },
  118: { message: 'Low Aux Analog 9 Warning', severity: 'warning' },
  119: { message: 'High Aux Analog 10 Shutdown', severity: 'error' },
  120: { message: 'High Aux Analog 10 Warning', severity: 'warning' },
  121: { message: 'Low Aux Analog 10 Shutdown', severity: 'error' },
  122: { message: 'Low Aux Analog 10 Warning', severity: 'warning' },
  123: { message: 'Low Suction Press Sensor Fault', severity: 'error' },
  124: { message: 'Low Disch Press Sensor Fault', severity: 'error' },
  125: { message: 'High Disch Press Sensor Fault', severity: 'error' },
  126: { message: 'Low Comp. Oil Press Sensor Fault', severity: 'error' },
  127: { message: 'High Comp. Oil Press Sensor Fault', severity: 'error' },
  128: { message: 'Low Disch Temp Sensor Fault', severity: 'error' },
  129: { message: 'Low Comp. Oil Temp Sensor Fault', severity: 'error' },
  130: { message: 'Low Separator Temp Sensor Fault', severity: 'error' },
  131: { message: 'Aux Input 11 Shutdown', severity: 'error' },
  132: { message: 'Aux Input 11 Warning', severity: 'warning' },
  133: { message: 'Aux Input 12 Shutdown', severity: 'error' },
  134: { message: 'Aux Input 12 Warning', severity: 'warning' },
  135: { message: 'Aux Input 13 Shutdown', severity: 'error' },
  136: { message: 'Aux Input 13 Warning', severity: 'warning' },
  137: { message: 'Aux Input 14 Shutdown', severity: 'error' },
  138: { message: 'Aux Input 14 Warning', severity: 'warning' },
  139: { message: 'Aux Input 15 Shutdown', severity: 'error' },
  140: { message: 'Aux Input 15 Warning', severity: 'warning' },
  141: { message: 'Aux Input 16 Shutdown', severity: 'error' },
  142: { message: 'Aux Input 16 Warning', severity: 'warning' },
  143: { message: 'Aux Input 17 Shutdown', severity: 'error' },
  144: { message: 'Aux Input 17 Warning', severity: 'warning' },
  145: { message: 'Aux Input 18 Shutdown', severity: 'error' },
  146: { message: 'Aux Input 18 Warning', severity: 'warning' },
  147: { message: 'Aux Input 19 Shutdown', severity: 'error' },
  148: { message: 'Aux Input 19 Warning', severity: 'warning' },
  149: { message: 'Aux Input 20 Shutdown', severity: 'error' },
  150: { message: 'Aux Input 20 Warning', severity: 'warning' },
  151: { message: 'High Aux Analog 11 Shutdown', severity: 'error' },
  152: { message: 'High Aux Analog 11 Warning', severity: 'warning' },
  153: { message: 'Low Aux Analog 11 Shutdown', severity: 'error' },
  154: { message: 'Low Aux Analog 11 Warning', severity: 'warning' },
  155: { message: 'High Aux Analog 12 Shutdown', severity: 'error' },
  156: { message: 'High Aux Analog 12 Warning', severity: 'warning' },
  157: { message: 'Low Aux Analog 12 Shutdown', severity: 'error' },
  158: { message: 'Low Aux Analog 12 Warning', severity: 'warning' },
  159: { message: 'High Aux Analog 13 Shutdown', severity: 'error' },
  160: { message: 'High Aux Analog 13 Warning', severity: 'warning' },
  161: { message: 'Low Aux Analog 13 Shutdown', severity: 'error' },
  162: { message: 'Low Aux Analog 13 Warning', severity: 'warning' },
  163: { message: 'High Aux Analog 14 Shutdown', severity: 'error' },
  164: { message: 'High Aux Analog 14 Warning', severity: 'warning' },
  165: { message: 'Low Aux Analog 14 Shutdown', severity: 'error' },
  166: { message: 'Low Aux Analog 14 Warning', severity: 'warning' },
  167: { message: 'High Aux Analog 15 Shutdown', severity: 'error' },
  168: { message: 'High Aux Analog 15 Warning', severity: 'warning' },
  169: { message: 'Low Aux Analog 15 Shutdown', severity: 'error' },
  170: { message: 'Low Aux Analog 15 Warning', severity: 'warning' },
  171: { message: 'High Aux Analog 16 Shutdown', severity: 'error' },
  172: { message: 'High Aux Analog 16 Warning', severity: 'warning' },
  173: { message: 'Low Aux Analog 16 Shutdown', severity: 'error' },
  174: { message: 'Low Aux Analog 16 Warning', severity: 'warning' },
  175: { message: 'High Aux Analog 17 Shutdown', severity: 'error' },
  176: { message: 'High Aux Analog 17 Warning', severity: 'warning' },
  177: { message: 'Low Aux Analog 17 Shutdown', severity: 'error' },
  178: { message: 'Low Aux Analog 17 Warning', severity: 'warning' },
  179: { message: 'High Aux Analog 18 Shutdown', severity: 'error' },
  180: { message: 'High Aux Analog 18 Warning', severity: 'warning' },
  181: { message: 'Low Aux Analog 18 Shutdown', severity: 'error' },
  182: { message: 'Low Aux Analog 18 Warning', severity: 'warning' },
  183: { message: 'High Aux Analog 19 Shutdown', severity: 'error' },
  184: { message: 'High Aux Analog 19 Warning', severity: 'warning' },
  185: { message: 'Low Aux Analog 19 Shutdown', severity: 'error' },
  186: { message: 'Low Aux Analog 19 Warning', severity: 'warning' },
  187: { message: 'High Aux Analog 20 Shutdown', severity: 'error' },
  188: { message: 'High Aux Analog 20 Warning', severity: 'warning' },
  189: { message: 'Low Aux Analog 20 Shutdown', severity: 'error' },
  190: { message: 'Low Aux Analog 20 Warning', severity: 'warning' },
  191: { message: 'Analog Board 1 Comms Shutdown', severity: 'error' },
  192: { message: 'Analog Board 2 Comms Shutdown', severity: 'error' },
  193: { message: 'Digital Board 1 Comms Shutdown', severity: 'error' },
  194: { message: 'Digital Board 2 Comms Shutdown', severity: 'error' },
  195: { message: 'DBS Comms Failure Shutdown', severity: 'error' },
  196: { message: 'Digital Board 1 Reset', severity: 'warning' },
  197: { message: 'Digital Board 2 Reset', severity: 'warning' },
  198: { message: 'Starting Failure - No Comp. Aux', severity: 'error' },
  199: { message: 'Starting Failure - Low Motor Amps', severity: 'error' },
  200: { message: 'VSD Comms Failure Warning', severity: 'warning' },
  201: { message: 'Starting Low Comp. Oil Press Shutdown', severity: 'error' },
  202: { message: 'Oil Pump Aux Failure', severity: 'error' },
  203: { message: 'Oil Pump 1 Aux Warning', severity: 'warning' },
  204: { message: 'Oil Pump 1 Aux Shutdown', severity: 'error' },
  205: { message: 'Oil Pump 2 Aux Warning', severity: 'warning' },
  206: { message: 'Oil Pump 2 Aux Shutdown', severity: 'error' },
  207: { message: 'Low Comp. Oil Press Warning', severity: 'warning' },
  208: { message: 'Low Comp. Oil Press Shutdown', severity: 'error' },
  209: { message: 'Missing Oil Press Shutdown C', severity: 'error' },
  210: { message: 'High Comp. Vib Warning - Suction', severity: 'warning' },
  211: { message: 'High Comp. Vib Warning - Disch', severity: 'warning' },
  212: { message: 'High Comp. Vib Shutdown - Suction', severity: 'error' },
  213: { message: 'High Comp. Vib Shutdown - Disch', severity: 'error' },
  214: { message: 'High Motor Vib Warning - Shaft Side', severity: 'warning' },
  215: { message: 'High Motor Vib Warning - Opp Shaft Side', severity: 'warning' },
  216: { message: 'High Motor Vib Shutdown - Shaft Side', severity: 'error' },
  217: { message: 'High Motor Vib Shutdown - Opp Shaft Side', severity: 'error' },
  218: { message: 'Comp. Aux Shutdown', severity: 'error' },
  220: { message: 'Oil Log Shutdown', severity: 'error' },
  221: { message: 'DBS - Current Unbalance', severity: 'error' },
  222: { message: 'DBS - RTD Temp', severity: 'error' },
  223: { message: 'DBS - Short Circuit', severity: 'error' },
  224: { message: 'DBS - Thermal Overload', severity: 'error' },
  225: { message: 'DBS - Shorted SCR', severity: 'error' },
  226: { message: 'DBS - Phase Loss', severity: 'error' },
  227: { message: 'DBS - Phase Reversal', severity: 'error' },
  228: { message: 'DBS - Jam', severity: 'error' },
  229: { message: 'DBS - HEATSINK Overtemp', severity: 'error' },
  230: { message: 'DBS - RTD Overtemp', severity: 'error' },
  231: { message: 'VSD Interface Board Power Supply Fault', severity: 'error' },
  233: { message: 'VSD Board Motor Current > 15%', severity: 'error' },
  234: { message: 'VSD Board Run Signal Fault', severity: 'error' },
  235: { message: 'VSD Interface Board to Panel Comms Loss', severity: 'error' },
  237: { message: 'VSD Initialization Fault', severity: 'error' },
  238: { message: 'VSD Stop Contacts Fault', severity: 'error' },
  241: { message: 'VSD High Phase B Inverter Baseplate Temp', severity: 'error' },
  242: { message: 'VSD High Phase C Inverter Baseplate Temp', severity: 'error' },
  243: { message: 'VSD Low Phase B Inverter Baseplate Temp', severity: 'error' },
  244: { message: 'VSD Low Phase C Inverter Baseplate Temp', severity: 'error' },
  247: { message: 'VSD High Phase A Inst Current', severity: 'error' },
  248: { message: 'VSD High Phase B Inst Current', severity: 'error' },
  249: { message: 'VSD High Phase C Inst Current', severity: 'error' },
  251: { message: 'VSD Phase A Gate Driver Fault', severity: 'error' },
  252: { message: 'VSD Phase B Gate Driver Fault', severity: 'error' },
  253: { message: 'VSD Phase C Gate Driver Fault', severity: 'error' },
  254: { message: 'VSD Single Phase Input Power Fault', severity: 'error' },
  257: { message: 'VSD 105% Motor Current Overload Fault', severity: 'error' },
  258: { message: 'VSD High DC Bus Voltage Fault', severity: 'error' },
  259: { message: 'VSD Logic Board Power Supply Fault', severity: 'error' },
  263: { message: 'VSD Low DC Bus Voltage Fault', severity: 'error' },
  264: { message: 'VSD DC Bus Voltage Imbalance Fault', severity: 'error' },
  265: { message: 'VSD High Internal Ambient Temp Fault', severity: 'error' },
  266: { message: 'VSD High Inverter Baseplate Temp Fault', severity: 'error' },
  267: { message: 'VSD Logic Board Processor Fault', severity: 'error' },
  268: { message: 'VSD Run Signal Fault', severity: 'error' },
  269: { message: 'VSD High Converter Heatsink Temp Fault', severity: 'error' },
  270: { message: 'VSD Invalid Current Scale Selection', severity: 'error' },
  271: { message: 'VSD Low Inverter Baseplate Temp Fault', severity: 'error' },
  272: { message: 'VSD Serial Comms Fault', severity: 'error' },
  273: { message: 'VSD Precharge Lockout Fault', severity: 'error' },
  274: { message: 'VSD Low Converter Heatsink Temp Fault', severity: 'error' },
  275: { message: 'VSD Current Imbalance Fault', severity: 'error' },
  276: { message: 'VSD Precharge - DC Bus Voltage Imbalance', severity: 'error' },
  277: { message: 'VSD Precharge - Low DC Bus Voltage 2', severity: 'error' },
  278: { message: 'VSD Precharge - Low DC Bus Voltage 1', severity: 'error' },
  311: { message: 'VSD Interface Board NovRAM Failure', severity: 'error' },
  331: { message: 'VSD High Phase A Inverter Baseplate Temp', severity: 'error' },
  332: { message: 'VSD Low Phase A Inverter Baseplate Temp', severity: 'error' },
  239: { message: 'Harmonic Filter Logic Board Or Comms Fault', severity: 'error' },
  240: { message: 'Harmonic Filter High Total Demand Distortion', severity: 'warning' },
  280: { message: 'Harmonic Filter High DC Bus Voltage Fault', severity: 'error' },
  281: { message: 'Harmonic Filter High Phase C Current Fault', severity: 'error' },
  282: { message: 'Harmonic Filter High Phase B Current Fault', severity: 'error' },
  283: { message: 'Harmonic Filter High Phase A Current Fault', severity: 'error' },
  284: { message: 'Harmonic Filter Phase Locked Loop Fault', severity: 'error' },
  286: { message: 'Harmonic Filter Logic Board Power Supply', severity: 'error' },
  295: { message: 'Harmonic Filter Precharge - High DC Bus Volt', severity: 'error' },
  296: { message: 'Harmonic Filter Precharge - Low DC Bus Volt', severity: 'error' },
  297: { message: 'Harmonic Filter DC Current Transformer 1', severity: 'error' },
  298: { message: 'Harmonic Filter DC Current Transformer 2', severity: 'error' },
  299: { message: 'Harmonic Filter High Baseplate Temp Fault', severity: 'error' },
  301: { message: 'Harmonic Filter Low DC Bus Voltage', severity: 'error' },
  305: { message: 'Harmonic Filter DC Bus Voltage Imbalance', severity: 'error' },
  306: { message: 'Harmonic Filter 110% Input Current Overload', severity: 'error' },
  307: { message: 'Harmonic Filter Run Signal Fault', severity: 'error' },
  313: { message: 'Harmonic Filter Serial Comms', severity: 'error' },
  314: { message: 'Harmonic Filter Input Frequency Out of Range', severity: 'error' },
  333: { message: 'High Disch Press Shutdown', severity: 'error' },
  334: { message: 'High Disch Press Warning', severity: 'warning' },
  335: { message: 'Process Stopped - See Event Log', severity: 'error' },
  336: { message: 'High Process Entering Temp Shutdown', severity: 'error' },
  337: { message: 'High Process Entering Temp Warning', severity: 'warning' },
  338: { message: 'Low Process Entering Temp Shutdown', severity: 'error' },
  339: { message: 'Low Process Entering Temp Warning', severity: 'warning' },
  340: { message: 'High Motor Temp Warning - Shaft Side', severity: 'warning' },
  341: { message: 'High Motor Temp Warning - Opp Shaft Side', severity: 'warning' },
  342: { message: 'High Motor Temp Shutdown - Shaft Side', severity: 'error' },
  343: { message: 'High Motor Temp Shutdown - Opp Shaft Side', severity: 'error' },
  344: { message: 'High Motor Stator 1 Temp Warning', severity: 'warning' },
  345: { message: 'High Motor Stator 1 Temp Shutdown', severity: 'error' },
  346: { message: 'High Motor Stator 2 Temp Warning', severity: 'warning' },
  347: { message: 'High Motor Stator 2 Temp Shutdown', severity: 'error' },
  348: { message: 'High Motor Stator 3 Temp Warning', severity: 'warning' },
  349: { message: 'High Motor Stator 3 Temp Shutdown', severity: 'error' },
  350: { message: 'Sequencing Slide Valve Failure Shutdown', severity: 'error' },
  351: { message: 'DBS Comms Failure Warning', severity: 'warning' },
  352: { message: 'Low Oil Differential 1 (Kobe)', severity: 'error' },
  353: { message: 'Low Oil Differential 2 (Kobe)', severity: 'error' },
  354: { message: 'High Oil Press (Kobe)', severity: 'error' },
  355: { message: 'High Comp. Oil Press Shutdown', severity: 'error' },
  356: { message: 'Coalescer Filter Differential Warning', severity: 'warning' },
  357: { message: 'Low Disch Press Shutdown', severity: 'error' },
  358: { message: 'High Oil Temp Sensor Fault', severity: 'error' },
  359: { message: 'High Separator Temp Sensor Fault', severity: 'error' },
  360: { message: 'Low Main Oil Inj Press Sensor Warning', severity: 'warning' },
  361: { message: 'High Main Oil Inj Press Sensor Warning', severity: 'warning' },
  362: { message: 'Low Economizer Press Sensor Warning', severity: 'warning' },
  363: { message: 'High Economizer Press Sensor Warning', severity: 'warning' },
  364: { message: 'Low Filter Press Sensor Warning', severity: 'warning' },
  365: { message: 'High Filter Press Sensor Warning', severity: 'warning' },
  366: { message: 'Liquid Injection Closed Warning', severity: 'warning' },
  367: { message: 'Low Disch Press Shutdown (RCSI)', severity: 'error' },
  368: { message: 'Low Bal Piston Press Sensor Warning', severity: 'warning' },
  369: { message: 'High Bal Piston Press Sensor Warning', severity: 'warning' },
  370: { message: 'Low System Disch Press Sensor Warning', severity: 'warning' },
  371: { message: 'High System Disch Press Sensor Warning', severity: 'warning' },
  372: { message: 'Low Suction Temp Sensor Warning', severity: 'warning' },
  373: { message: 'High Suction Temp Sensor Warning', severity: 'warning' },
  374: { message: 'Low Disch Temp Sensor Warning', severity: 'warning' },
  375: { message: 'High Disch Temp Sensor Warning', severity: 'warning' },
  376: { message: 'Low Oil Temp Comp. Sensor Warning', severity: 'warning' },
  377: { message: 'High Oil Temp Comp. Sensor Warning', severity: 'warning' },
  378: { message: 'Low Oil Separator Temp Sensor Warning', severity: 'warning' },
  379: { message: 'High Oil Separator Temp Sensor Warning', severity: 'warning' },
  380: { message: 'Low Vyper Coolant Temp. Sensor Warning', severity: 'warning' },
  381: { message: 'High Vyper Coolant Temp. Sensor Warning', severity: 'warning' },
  382: { message: 'Low Process Leaving Temp Sensor Warning', severity: 'warning' },
  383: { message: 'High Process Leaving Temp Sensor Warning', severity: 'warning' },
  384: { message: 'Low Process Entering Temp Sensor Warning', severity: 'warning' },
  385: { message: 'High Process Entering Temp Sensor Warning', severity: 'warning' },
  386: null,
  387: { message: 'Low Slide Valve Sensor Warning', severity: 'warning' },
  388: { message: 'Low Slide Stop Sensor Warning', severity: 'warning' },
  389: null,
  390: { message: 'High Vyper Coolant Temp Shutdown', severity: 'error' },
  391: { message: 'High Vyper Coolant Temp Warning', severity: 'warning' },
  392: { message: 'Low Vyper Coolant Temp Shutdown', severity: 'error' },
  393: { message: 'Low Vyper Coolant Temp Warning', severity: 'warning' },
  394: { message: 'Low Demand Pump Press Warning', severity: 'warning' },
  395: { message: 'Low Demand Pump Press Shutdown', severity: 'error' },
  396: { message: 'Separator Condensing Warning', severity: 'warning' },
  397: { message: 'Separator Condensing Shutdown', severity: 'error' },
  398: { message: 'Restart Lockout Shutdown', severity: 'error' },
  399: null,
  400: null,
  401: null,
  402: { message: 'Low Ext Setpt/Ext Inp Sig Sensor Warning', severity: 'warning' },
  403: { message: 'High Ext Setpt/Ext Inp Sig Sensor Warning', severity: 'warning' },
  404: { message: 'Low Motor Current Sensor Warning', severity: 'warning' },
  405: { message: 'High Motor Current Sensor Warning', severity: 'warning' },
  406: { message: 'Low RPM Sensor Warning', severity: 'warning' },
  407: { message: 'High RPM Sensor Warning', severity: 'warning' },
  408: { message: 'Low Kw Monitoring Sensor Warning', severity: 'warning' },
  409: { message: 'High Kw Monitoring Sensor Warning', severity: 'warning' },
  410: { message: 'Low EZ Cool LIOC Feedback Sensor Warning', severity: 'warning' },
  411: { message: 'High EZ Cool LIOC Feedback Sensor Warning', severity: 'warning' },
  412: { message: 'Low Aux AI 1 Sensor Warning', severity: 'warning' },
  413: { message: 'High Aux AI 1 Sensor Warning', severity: 'warning' },
  414: { message: 'Low Aux AI 2 Sensor Warning', severity: 'warning' },
  415: { message: 'High Aux AI 2 Sensor Warning', severity: 'warning' },
  416: { message: 'Low Aux AI 3 Sensor Warning', severity: 'warning' },
  417: { message: 'High Aux AI 3 Sensor Warning', severity: 'warning' },
  418: { message: 'Low Aux AI 4 Sensor Warning', severity: 'warning' },
  419: { message: 'High Aux AI 4 Sensor Warning', severity: 'warning' },
  420: { message: 'Low Aux AI 5 Sensor Warning', severity: 'warning' },
  421: { message: 'High Aux AI 5 Sensor Warning', severity: 'warning' },
  422: { message: 'Low Aux AI 6 Sensor Warning', severity: 'warning' },
  423: { message: 'High Aux AI 6 Sensor Warning', severity: 'warning' },
  424: { message: 'Low Aux AI 7 Sensor Warning', severity: 'warning' },
  425: { message: 'High Aux AI 7 Sensor Warning', severity: 'warning' },
  426: { message: 'Low Aux AI 8 Sensor Warning', severity: 'warning' },
  427: { message: 'High Aux AI 8 Sensor Warning', severity: 'warning' },
  428: { message: 'Low Aux AI 9 Sensor Warning', severity: 'warning' },
  429: { message: 'High Aux AI 9 Sensor Warning', severity: 'warning' },
  430: { message: 'Low Aux AI 10 Sensor Warning', severity: 'warning' },
  431: { message: 'High Aux AI 10 Sensor Warning', severity: 'warning' },
  432: { message: 'Low Aux AI 11 Sensor Warning', severity: 'warning' },
  433: { message: 'High Aux AI 11 Sensor Warning', severity: 'warning' },
  434: { message: 'Low Aux AI 12 Sensor Warning', severity: 'warning' },
  435: { message: 'High Aux AI 12 Sensor Warning', severity: 'warning' },
  436: { message: 'Low Aux AI 13 Sensor Warning', severity: 'warning' },
  437: { message: 'High Aux AI 13 Sensor Warning', severity: 'warning' },
  438: { message: 'Low Aux AI 14 Sensor Warning', severity: 'warning' },
  439: { message: 'High Aux AI 14 Sensor Warning', severity: 'warning' },
  440: { message: 'Low Aux AI 15 Sensor Warning', severity: 'warning' },
  441: { message: 'High Aux AI 15 Sensor Warning', severity: 'warning' },
  442: { message: 'Low Aux AI 16 Sensor Warning', severity: 'warning' },
  443: { message: 'High Aux AI 16 Sensor Warning', severity: 'warning' },
  444: { message: 'Low Aux AI 17 Sensor Warning', severity: 'warning' },
  445: { message: 'High Aux AI 17 Sensor Warning', severity: 'warning' },
  446: { message: 'Low Aux AI 18 Sensor Warning', severity: 'warning' },
  447: { message: 'High Aux AI 18 Sensor Warning', severity: 'warning' },
  448: { message: 'Low Aux AI 19 Sensor Warning', severity: 'warning' },
  449: { message: 'High Aux AI 19 Sensor Warning', severity: 'warning' },
  450: { message: 'Low Aux AI 20 Sensor Warning', severity: 'warning' },
  451: { message: 'High Aux AI 20 Sensor Warning', severity: 'warning' },
  452: { message: 'Low Manifold Press Sensor Warning', severity: 'warning' },
  453: { message: 'High Manifold Press Sensor Warning', severity: 'warning' },
  454: { message: 'Low Rem Capacity Position Sensor Warning', severity: 'warning' },
  455: { message: 'High Rem Capacity Position Sensor Warning', severity: 'warning' },
  456: { message: 'Low Liquid Level Sensor Warning', severity: 'warning' },
  457: { message: 'High Liquid Level Sensor Warning', severity: 'warning' },
  458: { message: 'High Process Leaving Temp Shutdown', severity: 'error' },
  459: { message: 'High Process Leaving Temp Warning', severity: 'warning' },
  460: { message: 'Low Process Leaving Temp Shutdown', severity: 'error' },
  461: { message: 'Low Process Leaving Temp Warning', severity: 'warning' },
  462: { message: 'Coalescer Filter Differential Warning', severity: 'warning' },
  463: { message: 'Condensing Water In Separator Warning (Off)', severity: 'warning' },
  464: { message: 'Condensing Water In Separator Warning (Running)', severity: 'warning' },
  465: null,
  466: { message: 'Manual Stop Shutdown (RSCI only)', severity: 'error' },
  467: { message: 'Remote Stop Shutdown (RCIS only)', severity: 'error' },
  468: { message: 'Vyper Fault Limit Reached Shutdown', severity: 'error' },
  469: { message: 'Control Oil Pump Aux Failure Shutdown', severity: 'error' },
  470: { message: 'Control Oil Pump 1 Aux Warning', severity: 'warning' },
  471: { message: 'Control Oil Pump 1 Aux Shutdown', severity: 'error' },
  472: { message: 'Control Oil Pump 2 Aux Warning', severity: 'warning' },
  473: { message: 'Control Oil Pump 2 Aux Shutdown', severity: 'error' },
  474: { message: 'Vilter MonoScrew Running Oil Press Shutdown', severity: 'error' },
  475: { message: 'Vilter MonoScrew Running Oil Press Warning', severity: 'warning' },
  476: { message: 'Vilter MonoScrew Changeover Oil Press Shutdown', severity: 'error' },
  477: { message: 'Vilter MonoScrew Low Pre-Lube Oil Press Shutdown', severity: 'error' },
};

/** Alarm code tables by compressor type. */
export const ALARMS_BY_COMPRESSOR_TYPE: Readonly<Record<string, CompressorAlarmTable>> = {
  [COMPRESSOR_TYPES.FRICK]: FRICK_ALARMS,
};
